import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert } from '@mui/material';
import ReactMarkdown from 'react-markdown';

const PrivacyPolicy = () => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPrivacyPolicy();
  }, []);

  const fetchPrivacyPolicy = async () => {
    try {
      setLoading(true);
      const response = await fetch('/PRIVACY_POLICY.md');
      if (!response.ok) {
        throw new Error('Failed to load privacy policy');
      }
      const text = await response.text();
      setContent(text);
    } catch (err) {
      setError('Failed to load privacy policy. Please try again later.');
      console.error('Error fetching privacy policy:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: '800px', mx: 'auto' }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{
          fontWeight: 700,
          mb: 4,
          textAlign: 'center',
          color: 'primary.main'
        }}
      >
        Privacy Policy
      </Typography>

      <Paper
        elevation={2}
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor: 'background.paper',
          '& h1': {
            fontSize: '2rem',
            fontWeight: 600,
            mb: 3,
            color: 'primary.main',
            borderBottom: '2px solid',
            borderColor: 'primary.main',
            pb: 1
          },
          '& h2': {
            fontSize: '1.5rem',
            fontWeight: 600,
            mt: 4,
            mb: 2,
            color: 'text.primary'
          },
          '& h3': {
            fontSize: '1.25rem',
            fontWeight: 500,
            mt: 3,
            mb: 2,
            color: 'text.primary'
          },
          '& p': {
            mb: 2,
            lineHeight: 1.6,
            color: 'text.primary'
          },
          '& ul, & ol': {
            mb: 2,
            pl: 3
          },
          '& li': {
            mb: 1,
            lineHeight: 1.5
          },
          '& strong': {
            fontWeight: 600,
            color: 'text.primary'
          },
          '& em': {
            fontStyle: 'italic'
          },
          '& blockquote': {
            borderLeft: '4px solid',
            borderColor: 'primary.main',
            pl: 2,
            py: 1,
            my: 2,
            backgroundColor: 'action.hover',
            fontStyle: 'italic'
          },
          '& code': {
            backgroundColor: 'grey.100',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.875rem'
          },
          '& pre': {
            backgroundColor: 'grey.100',
            p: 2,
            borderRadius: 1,
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            mb: 2
          },
          '& a': {
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          },
          '& hr': {
            my: 3,
            border: 'none',
            borderTop: '1px solid',
            borderColor: 'divider'
          }
        }}
      >
        <ReactMarkdown>{content}</ReactMarkdown>
      </Paper>
    </Box>
  );
};

export default PrivacyPolicy;